import type { OnboardingState, PanelKey } from "../lib/contracts";
import { PANEL_KEYS, panelForItem, panelTitle } from "../lib/contracts";

export interface OnboardingProgressProps {
  state: OnboardingState;
  activePanel: PanelKey;
  onSelectPanel: (p: PanelKey) => void;
}

export function OnboardingProgress({
  state, activePanel, onSelectPanel,
}: OnboardingProgressProps) {
  const complete = state.items.filter((i) => i.status === "complete").length;
  const incomplete = state.items.length - complete;
  const overallLabels: Record<string, string> = {
    not_started: "Not started",
    in_progress: "In progress",
    ready_to_complete: "Ready to complete",
    completed: "Completed",
    unknown: "Unknown",
  };

  // Group items by panel for the rail.
  const panelSummaries = PANEL_KEYS.map((p) => {
    const items = state.items.filter((it) => panelForItem(it.key) === p);
    const total = items.length;
    const done = items.filter((i) => i.status === "complete").length;
    return { p, total, done, allDone: total > 0 && done === total };
  });

  return (
    <nav
      aria-label="Onboarding progress"
      data-testid="onboarding-progress"
      className="rounded-lg border bg-white p-4 dark:bg-slate-900"
    >
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide text-slate-500">
          Overall status
        </p>
        <p
          className="text-base font-semibold"
          data-testid="onboarding-overall-status"
        >
          {overallLabels[state.overall_status]}
        </p>
      </div>
      <div className="mb-3" role="group" aria-label="Overall completion">
        <div className="flex items-baseline justify-between text-sm">
          <span>Completion</span>
          <span
            data-testid="onboarding-completion-pct"
            className="font-medium"
          >
            {state.completion_pct}%
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={state.completion_pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${state.completion_pct} percent complete`}
          className="mt-1 h-2 w-full rounded bg-slate-200 dark:bg-slate-700"
        >
          <div
            className="h-2 rounded bg-emerald-500"
            style={{ width: `${state.completion_pct}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-slate-500">
          <span data-testid="onboarding-complete-count">{complete}</span> complete
          {" · "}
          <span data-testid="onboarding-incomplete-count">{incomplete}</span> incomplete
        </p>
      </div>
      <ul className="space-y-1">
        {panelSummaries.map(({ p, total, done, allDone }) => {
          const isActive = p === activePanel;
          return (
            <li key={p}>
              <button
                type="button"
                aria-current={isActive ? "page" : undefined}
                data-testid={`onboarding-panel-tab-${p}`}
                onClick={() => onSelectPanel(p)}
                className={`w-full rounded-md px-3 py-2 text-left text-sm ${
                  isActive
                    ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                <span aria-hidden="true">{allDone ? "✓ " : "○ "}</span>
                {panelTitle(p)}
                <span className="ml-2 text-xs opacity-70">
                  {done}/{total}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
